import React from 'react';
import { Beer, Wine, Utensils, Cookie, Gift, Percent, Coffee, Medal, Droplet, Sparkles } from 'lucide-react';
import type { Freebie, RaceWeek, RaceWeekEvent, SourceRef } from '../types';
import { chicagoCityEvents } from './cityEvents';
import { chicagoSpectators } from './spectators';

// From context-chicago/race-weekend.md, context-chicago/freebies-refs/ and the race-week events artifact
// (compass_artifact_wf-b6f2822a-…, researched 2 Oct 2026). Brand events marked "listing" come from the
// marathon-weekend.com aggregator and some only give a ZIP code, so confirm on the host's page.
// Offers with only prior-year evidence (RPM, Adalina, iCRYO, Skydeck Ledge photo…) are deliberately left out,
// as are theWit's lobby station (likely hotel guests only) and the Cookie Bar (no confirmed address).

const OFFICIAL_EVENTS = 'https://www.chicagomarathon.com/event-info/race-weekend-events/';
const LISTING = 'https://www.marathon-weekend.com/chicago/2026';
const TRACKSMITH = 'https://www.tracksmith.com/journal/article/chicago-marathon-weekend-2026';

const cfm = (summary: string): SourceRef => ({
  title: 'Chicago Food Magazine — Where to carb load, 2026',
  url: 'https://www.chicagofoodmagazine.com/news/where-to-carbo-load-for-the-2026-chicago-marathon',
  summary,
});

const officialRaceEvents: RaceWeekEvent[] = [
  {
    date: '2026-10-08',
    time: '10:00–18:00',
    title: 'Abbott Health & Fitness Expo opens',
    kind: 'official',
    location: 'McCormick Place North, Level 3, Hall B1 · 2301 S Martin Luther King Dr',
    description:
      'Bib pickup in person only — government photo ID in your registration name plus the pickup ticket emailed by 30 Sep. No proxy pickup, no race-day pickup. Thursday is the quiet day; Tracksmith and Bandit stock sells out by Saturday. Allow 60–90 min. Grab a free pace tattoo at the Pace Team booth. Getting there: Metra Electric (the race’s pick), CTA Green Line to Cermak-McCormick Place or the #3 bus; rideshare drops at the East Building. Main-stage talk Fri 16:00: Dakotah Popehn (Shokz).',
    booking: 'Free entry · security screening, no large bags.',
    url: 'https://www.chicagomarathon.com/runners/expo-packet-pick-up/',
  },
  {
    date: '2026-10-09',
    time: '09:00–18:00',
    title: 'Expo · day 2',
    kind: 'official',
    location: 'McCormick Place North, Hall B1',
    description: 'Same as Thursday. Shirt exchange desk at the back — the Nike participant shirt runs short and boxy.',
    planned: true,
  },
  {
    date: '2026-10-09',
    time: 'Evening',
    title: 'Pre-race pasta dinner',
    kind: 'official',
    location: 'Millennium Hall · 11 N Michigan Ave',
    description:
      'All-you-can-eat salads, pasta and desserts, sponsored by Pasta Garofalo. Wheat pasta and no dietary info published — not a fit for the low-FODMAP load. Also runs Saturday.',
    booking: '$50 · reserve on OpenTable.',
    url: 'https://www.millenniumhallchicago.com/pastadinner',
  },
  {
    date: '2026-10-10',
    time: '07:30',
    title: 'Abbott Chicago 5K',
    kind: 'race',
    location: 'Grant Park',
    description: '3.1 miles past downtown landmarks with a finisher medal. Finish closes 09:00.',
    booking: 'Sold out — spectating.',
    url: 'https://www.chicago5k.com/event-information/',
    planned: true,
  },
  {
    date: '2026-10-10',
    time: '09:00–18:00',
    title: 'Expo · final day',
    kind: 'official',
    location: 'McCormick Place North, Hall B1',
    description:
      'Packet pickup closes at 18:00 sharp. Official finisher merchandise goes on sale today only — Nike Chicago, nike.com and the Nike App.',
  },
  {
    date: '2026-10-11',
    time: '05:30',
    title: 'Grant Park opens · gear check & corrals',
    kind: 'official',
    location: 'Gates on Michigan Ave: #1 Jackson · #2 Van Buren · #3–5 Ida B. Wells · #7 Harrison',
    description:
      'Your gate and gear-check colour are on your bib. Only the clear race-issued bag gets through security; no vests or backpacks; liquids in factory-sealed bottles. Columbus Dr crosswalks close at 07:30, 07:40 and 08:10.',
    url: 'https://cdn.chicagomarathon.com/app/uploads/2026/09/22133648/26-BACM-GRANT-PARK-SITE-MAP-9.22.26.pdf',
  },
  {
    date: '2026-10-11',
    time: '07:35',
    title: 'Bank of America Chicago Marathon · Wave 1',
    kind: 'race',
    location: 'Start: Columbus Dr at Monroe · Finish: Columbus Dr near Roosevelt',
    description:
      'Pros 07:30 · Wave 1 (A–E) 07:35, corrals close 07:20 · Wave 2 (F–J) 08:00, close 07:45 · Wave 3 (K–N) 08:35, close 08:10. Be in the corral ~20 min before it closes or you start at the back of your wave. 6:30 course limit.',
    url: 'https://www.chicagomarathon.com/runners/runner-information/race-day-timeline/',
    planned: true,
  },
  {
    date: '2026-10-11',
    time: '09:30–16:00',
    title: '27th Mile Post-Race Party',
    kind: 'official',
    location: 'Butler Field, Grant Park · Columbus & Jackson',
    description:
      'DJ, food for purchase, runner reunite towers (pick a letter with your people beforehand). Finishers 21+ trade the bib tear-off tab for a free beer — Goose Island 312, Goose IPA or Michelob ULTRA. Bring photo ID (passport).',
    url: OFFICIAL_EVENTS,
    planned: true,
  },
  {
    date: '2026-10-11',
    time: 'All day',
    title: 'Spectator cheer zones',
    kind: 'official',
    location: '8K Lincoln Park · Mile 13 N Wacker · Mile 15 Adams & Loomis · Mile 26 Michigan & Roosevelt',
    description:
      'For supporters: track on the official race app (TCS). CTA Red Line Grand (mile 1), Jackson (mile 2) and Chicago (mile 3.5) are on course; Roosevelt for the finish.',
    url: 'https://www.chicagomarathon.com/spectators/',
  },
];

const shakeouts: RaceWeekEvent[] = [
  {
    date: '2026-10-08',
    time: '18:30',
    title: '3RUN2 Thursday Night Social Run',
    kind: 'shakeout',
    location: 'Goose Island Taproom · 1800 W Fulton St',
    description: 'With New Balance, Propel and Shokz. Counts toward the Shokz Four-Star challenge.',
    url: LISTING,
  },
  {
    date: '2026-10-08',
    time: '18:30',
    title: 'Heartbreak Locals Night (Nike)',
    kind: 'shakeout',
    location: 'Heartbreak Hill · 2148 N Halsted St, Lincoln Park',
    description: 'Race-week social at Chicago’s Nike-backed run shop. Needed an RSVP and promised “special surprises” in 2025.',
    url: LISTING,
  },
  {
    date: '2026-10-09',
    time: '07:30',
    title: 'Brooks Run Club shakeout with Des Linden & Kara Goucher',
    kind: 'shakeout',
    location: 'Streeterville (60611) — address on the listing',
    description: 'The 2025 Brooks Friday shakeout came with coffee and breakfast. Brooks used RSVPify last year.',
    url: LISTING,
  },
  {
    date: '2026-10-09',
    time: '17:30',
    title: 'Peloton shakeout 5K',
    kind: 'shakeout',
    location: 'Caffè Oliva, Ohio Street Beach · 550 E Grand Ave',
    description:
      'Conversational-pace lakefront 5K with Tread instructor Kirsten Ferguson. Check-in 16:45, done by 19:30, bag drop, refreshments and light bites. Members and non-members welcome.',
    booking: 'Free · register in advance — limited, one per person. Clashes with the Bulls game.',
    url: 'https://pelotonshakeoutrun-chicago2026.splashthat.com/',
  },
  {
    date: '2026-10-10',
    time: '07:30',
    title: 'Heartbreak Shakeout powered by Nike Running',
    kind: 'shakeout',
    location: 'Heartbreak Hill · 2148 N Halsted St, Lincoln Park',
    description:
      'The atmosphere pick. 2025 had Nike pros; an earlier edition handed out free smoothies and collapsible bottles.',
    booking: 'Likely needs a Nike account RSVP.',
    url: LISTING,
  },
  {
    date: '2026-10-10',
    time: '08:00',
    title: 'Arc’teryx shakeout (Chicago Athlete)',
    kind: 'shakeout',
    location: 'Arc’teryx · 1007 N Rush St',
    description:
      '3-mile out-and-back on part of the course, tips from Chirine Njeim, then snacks, drinks and a DJ. Doors 07:30.',
    booking: 'Free · Eventbrite.',
    url: 'https://www.eventbrite.com/e/arcteryx-chicago-marathon-shakeout-run-tickets-1999600314368',
  },
  {
    date: '2026-10-10',
    time: '08:30',
    title: 'Believe in the Run × On (Fleet Feet)',
    kind: 'shakeout',
    location: 'Streeterville (60611) — address on the listing',
    description:
      'The freebie pick in prior years: in 2025 (with New Balance) the first 500 got a corduroy cap, plus DJ, shoe demos, Maurten and LMNT, no RSVP. On is the 2026 partner, so giveaways may differ.',
    url: LISTING,
  },
  {
    date: '2026-10-10',
    time: '08:30',
    title: 'Brooks Run Club shakeout with Anthony Rizzo',
    kind: 'shakeout',
    location: 'Streeterville (60611) — address on the listing',
    description: 'The ex-Cub is running the 2026 race. Likely RSVP.',
    url: LISTING,
  },
  {
    date: '2026-10-10',
    time: '08:30',
    title: 'lululemon shakeout',
    kind: 'shakeout',
    location: 'Streeterville (60611) — address on the listing',
    description:
      'Easy group shakeout. The 2025 version was a 5K from the Rush St store with refreshments and light bites.',
    url: 'https://www.marathon-weekend.com/chicago/2026',
    planned: true,
  },
  {
    date: '2026-10-10',
    time: '09:00',
    title: 'Tracksmith pre-race shakeout 5K',
    kind: 'shakeout',
    location: 'Chicago Athletic Association · 12 S Michigan Ave',
    description: 'Meet 09:00, 5K leaves 09:30 — route posted on Strava. 2025 had water and bananas but no bag drop.',
    booking: 'Free · sign up on the Tracksmith page.',
    url: 'https://www.tracksmith.com/events/chicago-2026-pre-race-shakeout',
  },
  {
    date: '2026-10-10',
    time: '09:00–11:00',
    title: 'Marathon Handbook × Zappos shakeout',
    kind: 'shakeout',
    location: 'Goddess and the Baker · 181 W Madison St',
    description: '5K loop around Millennium Park, then coffee, pastries and “some goodies”. 18+.',
    booking: 'Free · Eventbrite (reported sold out).',
    url: 'https://www.eventbrite.com/e/2002027257420',
  },
  {
    date: '2026-10-10',
    time: '09:30',
    title: '3RUN2 × Endorphins shakeout',
    kind: 'shakeout',
    location: 'Goose Island Salt Shed Pub · 1221 W Blackhawk St',
    description:
      '3 miles. Sources disagree on the time (07:00, 08:30, 09:30 or 10:00) — check 3RUN2’s Instagram on Friday night.',
    url: 'https://app.endorphinsrunning.com/event/6a32cdf200c67a2d6b321f9e',
  },
  {
    date: '2026-10-10',
    time: '10:00',
    title: 'ASICS shakeout with Kofuzi & Run With Kenyans',
    kind: 'shakeout',
    location: 'South Loop (60605) — address on the listing',
    description: 'Fleet Feet-hosted. The 2024 version had a breakfast food truck.',
    url: LISTING,
  },
];

const brandEvents: RaceWeekEvent[] = [
  {
    date: '2026-10-07',
    time: '12:00–17:00',
    title: 'Tracksmith pop-up opens (to Mon 12)',
    kind: 'brand',
    location: 'Chicago Athletic Association · 12 S Michigan Ave',
    description: 'Hours: Wed 12–5, Thu–Sat 10–6, Sun 12–5, Mon 9–6. Hub for the shakeout, after-party and poster stamping.',
    url: TRACKSMITH,
  },
  {
    date: '2026-10-08',
    time: 'Thu–Mon',
    title: 'Brooks Hyperion House (to Mon 12)',
    kind: 'brand',
    location: 'Streeterville (60611) — address on the listing',
    description:
      '2025 brought shoe-trial giveaways, Monday medal photos and finisher scarves while they lasted, no RSVP. 2026 details not yet published.',
    url: 'https://www.brooksrunning.com/en_us/chicago-hyperion-house/',
  },
  {
    date: '2026-10-09',
    time: 'Fri–Mon',
    title: 'Endorphins Fuel Bar powered by New Balance (to Mon 12)',
    kind: 'brand',
    location: 'Loop (60602) — address on the listing',
    description:
      'With Mush, Throne Sport Coffee, SiS and Shokz. In 2025: free coffee, fuel and NB hats while supplies lasted.',
    url: LISTING,
  },
  {
    date: '2026-10-09',
    time: 'Fri–Mon',
    title: 'PUMA NITRO Lab (to Mon 12)',
    kind: 'brand',
    location: 'Streeterville (60611) — address on the listing',
    description: 'Metric Coffee is a partner, so free coffee is likely. Medal engraving Mon 10:00.',
    url: LISTING,
  },
  {
    date: '2026-10-09',
    time: '17:00',
    title: 'Nobody Asked Us — live with Des Linden & Kara Goucher',
    kind: 'brand',
    location: 'Loop (60603)',
    description: 'Live podcast taping (Brooks / Fleet Feet). The 2024 taping cost $20, so don’t assume it’s free.',
    url: LISTING,
  },
  {
    date: '2026-10-09',
    time: '18:00',
    title: 'Off The Rails LIVE (CITIUS Mag × Under Armour)',
    kind: 'brand',
    location: 'South Loop (60605)',
    description: 'Live show the night before the big shakeout morning.',
    url: LISTING,
  },
  {
    date: '2026-10-10',
    time: '13:00',
    title: 'Eataly Marathon Dinner',
    kind: 'brand',
    location: 'Eataly Chicago · 43 E Ohio St (listing gives no address)',
    description: 'Afternoon carb-load meal. Wheat pasta and garlic-heavy sauces, so not one for the low-FODMAP plan.',
    url: LISTING,
  },
  {
    date: '2026-10-11',
    time: '12:00–17:00',
    title: 'Tracksmith after-party & poster stamping',
    kind: 'brand',
    location: 'Chicago Athletic Association · 12 S Michigan Ave',
    description:
      'A drink and a free poster stamped with your finish time (limited — sign up to speed things up). Paid hoodie chain-stitching. Stamping continues Mon 12, 09:00–18:00.',
    url: TRACKSMITH,
    planned: true,
  },
  {
    date: '2026-10-11',
    time: '16:00',
    title: 'Endorphins after-party',
    kind: 'brand',
    location: 'River North (60654)',
    description: 'In 2025 finishers got a free drink with their medal at Electric Shuffle.',
    url: LISTING,
  },
  {
    date: '2026-10-11',
    time: '17:00',
    title: 'Heartbreak × Nike after-party',
    kind: 'brand',
    location: 'Venue to be announced',
    description: 'Heartbreak also runs a cheer zone at mile 20.5.',
    url: LISTING,
  },
  {
    date: '2026-10-12',
    time: '10:00',
    title: 'Free medal engraving — lululemon & PUMA',
    kind: 'brand',
    location: 'Streeterville / Mag Mile (60611) — addresses on the listing',
    description:
      'Lines have run to about an hour in past years, so go at opening. FP Movement engraves from 11:00 (60622); Altra / Fleet Feet run a Recovery Monday at 10:00 (60605). Free/RSVP terms not yet confirmed for 2026.',
    url: LISTING,
    planned: true,
  },
];

const freebies: Freebie[] = [
  {
    name: '27th Mile Party',
    rank: 1,
    offer: 'Free beer — 312, Goose IPA or Michelob ULTRA',
    icon: <Beer size={20} />,
    category: 'drink',
    neighbourhood: 'Grant Park · Butler Field',
    locations: 'Butler Field, Columbus Dr & Jackson Dr — straight out of the finish chute',
    proof: 'bib',
    caveats: '21+ with photo ID · bib tear-off tab · beer served until 15:30',
    description:
      'Your bib has a tear-off tab good for one free beer at the post-race party: Goose Island 312, Goose IPA or Michelob ULTRA. Extra beer tickets are on sale 09:30–15:00. The reunite towers are here too, so it doubles as the meeting point. Rehydrate before you drink it.',
    image: '/images/chicago/butler-field-party.jpg',
    status: 'verified',
    refs: [
      {
        title: 'Chicago Marathon — race weekend events',
        url: OFFICIAL_EVENTS,
        summary: 'Official: 09:30–16:00, finishers 21+ redeem the bib tab for one Goose Island 312.',
      },
    ],
    refsSummary: 'Straight from the race organiser. Bring photo ID — the tab alone isn’t enough.',
  },
  {
    name: 'Goose Island bars',
    rank: 6,
    offer: 'Free Goose Island 312 — first 312 runners per bar',
    icon: <Beer size={20} />,
    category: 'drink',
    neighbourhood: 'Streeterville · Loop · Fulton Market · Near North · Old Town',
    locations:
      "Lizzie McNeill's, 400 N McClurg Ct · Millennium Hall, 11 N Michigan Ave · Goose Island Taproom, 1800 W Fulton St · Salt Shed Pub, 1221 W Blackhawk St · Scout Waterhouse + Kitchen, 1419 N Wells St",
    proof: 'medal',
    caveats: 'First 312 medal-wearing runners at each bar · Sun 11 Oct · hours not published',
    description:
      "Five official post-race celebrations, each giving the first 312 runners wearing a 2026 medal a free 312. Millennium Hall (11 N Michigan) is the closest to the finish.",
    image: '/images/chicago/perks/goose-island-taproom.jpg',
    status: 'verified',
    refs: [
      {
        title: 'Chicago Marathon — race weekend events',
        url: OFFICIAL_EVENTS,
        summary: 'Official list of the five Goose Island Post-Race Participant Celebrations and the first-312 rule.',
      },
    ],
    refsSummary: 'Official race page. Capped at 312 runners per site, so go early.',
  },
  {
    name: 'Guinness Open Gate Brewery',
    rank: 5,
    offer: 'Free engraved pint glass + $2.62 pints',
    icon: <Beer size={20} />,
    category: 'drink',
    neighbourhood: 'Fulton Market',
    locations: '901 W Kinzie St, 60607',
    proof: 'medal',
    caveats: 'Glass for the first 100 medal-holders only · race day, 09:00–23:00',
    description:
      'The first 100 finishers with a medal get an engraved Guinness pint glass, and Chicago-exclusive beers are $2.62 a pint for everyone. Get there early for the glass.',
    status: 'verified',
    image: '/images/chicago/perks/guinness-open-gate.jpg',
    refs: [cfm('Engraved pint glass for the first 100 runners with a medal; $2.62 pints all day Sunday.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine); the brewery site confirms address and hours only.',
  },
  {
    name: 'Toro (Fairmont)',
    rank: 11,
    offer: 'Free margarita, bubbles or draft beer with any purchase',
    icon: <Wine size={20} />,
    category: 'drink',
    neighbourhood: 'Lakeshore East · near the finish',
    locations: 'Fairmont Chicago Millennium Park, 200 N Columbus Dr, 60601',
    proof: 'medal',
    caveats: 'With a purchase · one per person · Sun 11 & Mon 12',
    description:
      "Richard Sandoval's Latin restaurant in the Fairmont, a short walk from Grant Park. Show the medal for a classic margarita, a glass of bubbles or a draft beer alongside anything you order.",
    status: 'verified',
    image: '/images/chicago/perks/toro.jpg',
    refs: [cfm('Complimentary margarita, bubbles or draft beer with purchase; valid 11 and 12 Oct; one per person.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: "Michael Jordan's Steak House",
    rank: 10,
    offer: 'Free beer',
    icon: <Beer size={20} />,
    category: 'drink',
    neighbourhood: 'Magnificent Mile',
    locations: 'InterContinental, 505 N Michigan Ave, 60611',
    proof: 'medal-or-bib',
    caveats: 'Sun 11 & Mon 12 · unclear whether a purchase is needed',
    description: 'A free beer at the bar with your bib or medal, on the Mag Mile.',
    status: 'verified',
    image: '/images/chicago/perks/michael-jordans.jpg',
    refs: [cfm('Free beer for runners with bib or medal, 11–12 Oct.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'Casa Chi',
    rank: 14,
    offer: 'Free beer or sparkling wine with an entree',
    icon: <Wine size={20} />,
    category: 'drink',
    neighbourhood: 'Magnificent Mile',
    locations: 'InterContinental, 505 N Michigan Ave, 60611',
    proof: 'medal-or-bib',
    caveats: 'Entree purchase required · Sun 11 & Mon 12 · opens 14:00',
    description: 'Mexican kitchen in the same building as Michael Jordan’s. Order a main, get a beer or sparkling wine on the house.',
    image: '/images/chicago/perks/casa-chi.jpg',
    status: 'verified',
    refs: [cfm('Complimentary beer or sparkling wine with entree purchase, 11–12 Oct.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'The Gwen',
    rank: 12,
    offer: 'Free glass of bubbly + $5 off entrees',
    icon: <Wine size={20} />,
    category: 'drink',
    neighbourhood: 'Mag Mile / River North',
    locations: '521 N Rush St, 60611',
    proof: 'medal-or-bib',
    caveats: '21+ · Sun 11 into Mon 12 · entree discount from 14:00 · swag bag is hotel guests only',
    description: 'Art-deco hotel bar on Rush St. Bubbly on the house at the bar, and $5 off selected mains.',
    image: '/images/chicago/perks/the-gwen.jpg',
    status: 'verified',
    refs: [cfm('Free glass of bubbly at the bar and $5 off selected entrees for runners.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'La Grande Boucherie',
    rank: 13,
    offer: 'Free glass of champagne when dining',
    icon: <Wine size={20} />,
    category: 'drink',
    neighbourhood: 'River North',
    locations: '431 N Dearborn St, 60654',
    proof: 'medal',
    caveats: 'Must be dining · Sun 11 only',
    description: 'Grand Parisian brasserie in River North. A glass of champagne with your race-day dinner.',
    image: '/images/chicago/perks/la-grande-boucherie.jpg',
    status: 'verified',
    refs: [cfm('Complimentary glass of champagne for medal-holders dining on 11 Oct.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'Pizzeria Portofino',
    rank: 7,
    offer: 'Free Giuliana Prosecco toast',
    icon: <Wine size={20} />,
    category: 'drink',
    neighbourhood: 'River North · riverfront',
    locations: '317 N Clark St, 60654',
    proof: 'bib',
    caveats: '21+ · Sat 10 & Sun 11 · Sunday 11:30–21:00',
    description:
      'Riverside Italian with a post-race prosecco toast for runners and their supporters. Pre-race they offer double pasta portions — skip that, it is wheat pasta in garlicky sauce.',
    status: 'verified',
    image: '/images/chicago/perks/pizzeria-portofino.jpg',
    refs: [
      {
        title: 'Pizzeria Portofino — official',
        url: 'https://www.pizzeriaportofino.com/',
        summary: 'Homepage promotes the marathon prosecco toast for runners and supporters, 10–11 Oct.',
      },
      cfm('Complimentary Giuliana Prosecco pour; show your bib.'),
    ],
    refsSummary: 'Confirmed on the venue’s own site and by Chicago Food Magazine.',
  },
  {
    name: 'Tanta',
    rank: 4,
    offer: 'Free Cebiche Clásico',
    icon: <Utensils size={20} />,
    category: 'food',
    neighbourhood: 'River North',
    locations: '118 W Grand Ave, 60654',
    proof: 'bib',
    caveats: 'Dine-in · Sun 11',
    description:
      "Gastón Acurio's Peruvian restaurant. The classic cebiche — fish cured in leche de tigre — free with your bib.",
    status: 'verified',
    image: '/images/chicago/perks/tanta.jpg',
    refs: [cfm('Complimentary Cebiche Clásico for runners showing their bib on race day.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: "Lil' Ba-Ba-Reeba!",
    rank: 15,
    offer: 'Free dessert tapa',
    icon: <Cookie size={20} />,
    category: 'treat',
    neighbourhood: 'River North',
    locations: '441 N Clark St, 60654',
    proof: 'medal-or-bib',
    caveats: 'Dine-in · one per runner · race weekend',
    description: 'River North outpost of the Lincoln Park tapas institution. Dessert on the house with a bib or medal.',
    image: '/images/chicago/perks/lil-ba-ba-reeba.jpg',
    status: 'verified',
    refs: [cfm('Free dessert tapa for runners, race weekend, one per runner.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'Cafe Ba-Ba-Reeba!',
    rank: 16,
    offer: 'Free dessert tapa',
    icon: <Cookie size={20} />,
    category: 'treat',
    neighbourhood: 'Lincoln Park',
    locations: '2024 N Halsted St, 60614',
    proof: 'medal-or-bib',
    caveats: 'Dine-in · one per runner · Sun 11',
    description: 'The original Spanish tapas bar on Halsted, open since 1985.',
    image: '/images/chicago/perks/cafe-ba-ba-reeba.jpg',
    status: 'verified',
    refs: [cfm('Free dessert tapa on race day, one per runner.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'Mon Ami Gabi',
    rank: 18,
    offer: 'Free profiterole with an entree',
    icon: <Cookie size={20} />,
    category: 'treat',
    neighbourhood: 'Lincoln Park',
    locations: '2300 N Lincoln Park W, 60614',
    proof: 'medal-or-bib',
    caveats: 'Entree required · Sun 11 only · the old 26.2% discount is not running in 2026',
    description: 'French bistro by the park. Order a main and a profiterole comes free.',
    status: 'verified',
    image: '/images/chicago/perks/mon-ami-gabi.jpg',
    refs: [cfm('One free profiterole with entree purchase on race day.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'Tracksmith',
    rank: 2,
    offer: 'Free poster stamped with your finish time',
    icon: <Gift size={20} />,
    category: 'treat',
    neighbourhood: 'Loop · Michigan Ave',
    locations: 'Chicago Athletic Association, 12 S Michigan Ave, 60603',
    proof: 'medal',
    caveats: 'Sun 12:00–17:00 · Mon 12 09:00–18:00 · limited stock — signing up speeds things up but doesn’t guarantee one',
    description:
      'Tracksmith’s race-week pop-up stamps a commemorative poster with your finish time. Sunday is the after-party with drinks and hoodie customisation.',
    image: '/images/chicago/chicago-athletic-association.jpg',
    status: 'verified',
    refs: [
      {
        title: 'Tracksmith — Chicago Marathon weekend 2026',
        url: 'https://www.tracksmith.com/journal/article/chicago-marathon-weekend-2026',
        summary: 'Official Tracksmith page: pop-up hours, community 5K, after-party and poster stamping.',
      },
    ],
    refsSummary: 'Straight from Tracksmith.',
  },
  {
    name: 'Kindling',
    rank: 19,
    offer: '20% off the bill — a discount, nothing free',
    icon: <Percent size={20} />,
    category: 'food',
    neighbourhood: 'Loop · Willis Tower',
    locations: '202 S Franklin St, 60606 (base of Willis Tower)',
    proof: 'medal',
    caveats: 'Fri 9, Sat 10 & Mon 12 · closed Sunday',
    description:
      'Wood-fired restaurant at the foot of Willis Tower. Closed on race day, so it is a Monday post-race lunch — or after the Skydeck.',
    status: 'verified',
    image: '/images/chicago/perks/kindling.jpg',
    refs: [cfm('20% off the bill with a marathon medal on 9, 10 and 12 Oct.')],
    refsSummary: 'Single 2026 source (Chicago Food Magazine).',
  },
  {
    name: 'Pace Team tattoo',
    rank: 8,
    offer: 'Free pace tattoo + free pace group',
    icon: <Sparkles size={20} />,
    category: 'treat',
    neighbourhood: 'Expo · McCormick Place',
    locations: 'Pace Team booth, Abbott Health & Fitness Expo, McCormick Place North, Hall B1',
    proof: 'bib',
    caveats: 'Expo Thu 8–Sat 10 · pace groups 2:55 to 6:00',
    description:
      'A temporary split tattoo for your goal time, and the official pace groups are free to join. Pick it up at bib collection on Thursday.',
    image: '/images/chicago/perks/expo.jpg',
    status: 'verified',
    refs: [
      {
        title: 'Chicago Marathon — Pace Team',
        url: 'https://www.chicagomarathon.com/runners/runner-information/pace-team-information/',
        summary: 'Official: free pace groups from 2:55 to 6:00; tattoos at the expo booth.',
      },
    ],
    refsSummary: 'Straight from the race organiser.',
  },
  {
    name: 'Culligan finisher bottle',
    rank: 9,
    offer: 'Free reusable aluminium water bottle at the finish',
    icon: <Droplet size={20} />,
    category: 'recovery',
    neighbourhood: 'Finish line · Grant Park',
    locations: 'Finish area, Columbus Dr',
    proof: 'race-number',
    caveats: 'Handed out in 2025; expected again under Culligan’s multi-year deal',
    description:
      'Every 2025 finisher got a co-branded aluminium bottle of Culligan water. The sponsorship runs for multiple years and Culligan also added four refill stations on the 2026 course, so expect it again.',
    image: '/images/chicago/marathon-finish.jpg',
    status: 'likely',
    refs: [
      {
        title: 'Chicago Marathon — 2025 FAQ',
        url: 'https://www.chicagomarathon.com/participant-information/frequently-asked-questions',
        summary: '2025: every finisher received a reusable aluminium bottle of Culligan water.',
      },
    ],
    refsSummary: '2025 precedent plus a multi-year sponsor deal — not yet confirmed for 2026.',
  },
  {
    name: 'Medal Monday engraving',
    rank: 3,
    offer: 'Free medal engraving',
    icon: <Medal size={20} />,
    category: 'treat',
    neighbourhood: 'Streeterville / Mag Mile · Wicker Park',
    locations: 'lululemon and PUMA NITRO Lab (60611) from 10:00 · FP Movement (60622) from 11:00',
    proof: 'medal',
    caveats: 'Mon 12 Oct · up to ~1 h queues in past years · 2026 free/RSVP terms not confirmed',
    description:
      'The free alternative to the official itab plate, which is a paid add-on mailed after the race. Go at opening. PUMA’s lab partners with Metric Coffee, so there may be coffee while you wait.',
    image: '/images/chicago/mag-mile.jpg',
    status: 'likely',
    refs: [
      {
        title: 'Marathon Weekend — Chicago 2026 listing',
        url: LISTING,
        summary: 'Lists PUMA and lululemon engraving at 10:00 and FP Movement at 11:00 on Mon 12 Oct.',
      },
    ],
    refsSummary: 'Third-party aggregator listing; terms not on the brands’ own pages yet.',
  },
  {
    name: 'Endorphins Fuel Bar',
    rank: 17,
    offer: 'Free coffee, fuel and a hat (2025 giveaway)',
    icon: <Coffee size={20} />,
    category: 'drink',
    neighbourhood: 'Millennium Park',
    locations: 'Momentum Coffee, Millennium Park (per the 2026 Fuel Bar banner; 2025 was 435 N Michigan Ave)',
    proof: 'none',
    caveats: 'Open Fri 9–Mon 12 · 2026 giveaways not confirmed · hats while supplies last',
    description:
      'A New Balance-backed pop-up with Throne Sport Coffee, SiS and Mush. Last year it handed out free coffee, fuel samples and commemorative NB hats.',
    image: '/images/bean.jpg',
    status: 'likely',
    refs: [
      {
        title: 'Marathon Weekend — Endorphins Fuel Bar 2025',
        url: 'https://www.marathon-weekend.com/chicago/2025/events/endorphins-chicago-fuel-bar-marathon-weekend',
        summary: '2025 details: free coffee, fuel and NB hats while supplies lasted.',
      },
      {
        title: 'Marathon Weekend — Chicago 2026 listing',
        url: LISTING,
        summary: 'Confirms the pop-up for 9–12 Oct 2026.',
      },
    ],
    refsSummary: 'Pop-up confirmed for 2026; giveaways are 2025 precedent.',
  },

];

export const chicagoRaceWeek: RaceWeek = {
  lede: 'Expo, shakeout runs, post-race parties and what else is on in the city — plus every finisher perk with a 2026 source. Bring your bib and medal everywhere on Sunday and Monday.',
  events: [...officialRaceEvents, ...shakeouts, ...brandEvents, ...chicagoCityEvents],
  freebies,
  spectators: chicagoSpectators,
  freebiesLede:
    'Chicago’s perks lean to drinks rather than free meals. Most rest on a single 2026 article, so treat them as likely and confirm at the door.',
  footnote: (
    <>
      <strong>Always verify at the venue.</strong> Most offers run Sunday 11 Oct only, some into Monday 12. Drinks
      need photo ID (21+). Nothing confirmed for recovery, massage or transport this year, and there’s no free transit
      for runners — prior-year deals (RPM, Adalina, iCRYO, the Skydeck Ledge photo) have no 2026 source. More
      restaurant deals usually land in race week: check Time Out, Block Club and Axios on Thu 8, and each brand’s
      Instagram for late “first 500” giveaways.
    </>
  ),
};
